import { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';

import { Alert, Button, Modal, setSubmitError, toast, useAppForm } from '@keboola/design';

import { useAuth } from '../../../auth/context';
import { getApiErrorMessage } from '../../auth/errors';
import { MFA_CODE_LENGTH, mfaFormSchema } from '../../auth/schemas';
import { formValidators } from '../../auth/validate';
import { buildOtpauthUri, chunkSecret } from '../otpauth';
import { useBeginMfaEnrolment, useConfirmMfa } from '../queries';

const FORM_ID = 'confirm-mfa';

type Props = {
  /** The button that starts enrolment. "Change device" once the user is already enrolled. */
  label: string;
  variant?: 'primary' | 'secondary';
  size?: 'small' | 'medium' | 'large';
};

/**
 * TOTP enrolment: a button that requests a secret, then a modal that shows it and confirms the first code.
 *
 * - Shared by the MFA gate and the profile page so the two cannot drift and lock people out.
 * - QR from `qrcode.react`: `@keboola/design` has no QR encoder.
 */
export function MfaEnrolment({ label, variant = 'primary', size = 'medium' }: Props) {
  const { identity } = useAuth();
  const [secret, setSecret] = useState<string | null>(null);
  // Secret behind a toggle: shown next to the QR, people transcribe it instead of scanning.
  const [revealed, setRevealed] = useState(false);
  const begin = useBeginMfaEnrolment();
  const confirm = useConfirmMfa();

  const close = () => {
    setSecret(null);
    setRevealed(false);
  };

  const form = useAppForm({
    defaultValues: { code: '' },
    validators: formValidators(mfaFormSchema),
    canSubmitWhenInvalid: true,
    onSubmit: async ({ value, formApi }) => {
      try {
        await confirm.mutateAsync(value.code);
        toast.success('Multi-factor authentication is set up.');
        // No navigation: the invalidated profile query lifts the MFA gate.
        close();
        form.reset();
      } catch (error) {
        setSubmitError(formApi, getApiErrorMessage(error, 'That code was not accepted.'));
      }
    },
  });

  const email = identity?.email ?? '';

  return (
    <>
      {begin.isError && (
        <Alert variant="error" title="Could not start setup">
          {getApiErrorMessage(begin.error, 'Please try again.')}
        </Alert>
      )}

      <Button
        variant={variant}
        size={size}
        isLoading={begin.isPending}
        // `isLoading` does not block the click; a second enrolment would swap the secret mid-scan.
        disabled={begin.isPending}
        onClick={() => {
          begin.mutate(undefined, {
            onSuccess: (secretCode) => {
              setSecret(secretCode);
              // Component state, not `begin.data`: keeps the secret out of the `MutationCache`.
              begin.reset();
            },
          });
        }}
      >
        {label}
      </Button>

      <Modal
        open={secret !== null}
        onOpenChange={(open) => {
          if (!open) {
            close();
            form.reset();
          }
        }}
      >
        <Modal.Content size="medium">
          <Modal.Header description="Add the Developer Portal to your authenticator app, then enter the code it shows.">
            Confirm MFA setup
          </Modal.Header>
          <Modal.Body>
            <div className="tw:flex tw:flex-col tw:items-center tw:gap-3">
              {revealed ? (
                <>
                  <p className="tw:m-0 tw:text-center tw:text-sm tw:text-muted-foreground">
                    Type this time-based key into your authenticator app, without the spaces.
                  </p>
                  {/* Grouped in fours for transcription. */}
                  <code className="tw:font-mono tw:text-sm tw:break-all tw:text-foreground">
                    {secret !== null && chunkSecret(secret)}
                  </code>
                  <Button variant="invisible" size="small" onClick={() => setRevealed(false)}>
                    Show the QR code instead
                  </Button>
                </>
              ) : (
                <>
                  {/* Hardcoded `bg-white`: a QR must be dark-on-light to scan, even in dark mode. */}
                  <div className="tw:rounded-md tw:bg-white tw:p-3">
                    {secret !== null && (
                      <QRCodeSVG value={buildOtpauthUri(email, secret)} level="H" size={192} />
                    )}
                  </div>
                  <Button variant="invisible" size="small" onClick={() => setRevealed(true)}>
                    Can&apos;t scan the code? Reveal it
                  </Button>
                </>
              )}
            </div>

            <form.AppForm>
              <form.FormBody id={FORM_ID}>
                <form.AppField name="code">
                  {(field) => (
                    <field.OtpInputField
                      label="Verification code"
                      required
                      maxLength={MFA_CODE_LENGTH}
                    />
                  )}
                </form.AppField>
                <form.SubmitErrorAlert />
              </form.FormBody>
            </form.AppForm>
          </Modal.Body>
          <Modal.Footer>
            <Modal.Close asChild>
              <Button type="button" variant="secondary">
                Cancel
              </Button>
            </Modal.Close>
            <form.AppForm>
              <form.SubmitButton form={FORM_ID}>Confirm</form.SubmitButton>
            </form.AppForm>
          </Modal.Footer>
        </Modal.Content>
      </Modal>
    </>
  );
}
